import { useFirstPromoter } from "@/feature/firstPromoter/hooks/useFirstPromoter.hook";
import { useMetaPixel } from "../hooks/useMetaPixel";
import { useSearchParams } from "react-router-dom";
import { postRegisterWithOffer } from "@/services/auth/authAxios/authAxios";
import { FirstPromoter } from "@/feature/firstPromoter/services/firstPromoter.service";
import { decodeTokenService } from "@/services/decodeTokenService";
import { useState } from "react";
import { Mail, ArrowRight, AlertTriangle, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { dictionary, Language } from "../utils/dictionary";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import FluxiLogo from "../assets/svg/FluxiLogoWhite.svg";

const OFFER_UNAVAILABLE_TRIGGERS = new Set([
  "invalid_promo_code",
  "promo_code_invalid",
  "promo_code_not_allowed_for_email",
  "user_already_has_membership",
]);

const LandingOfferPage = () => {
  const [searchParams] = useSearchParams();
  const { trackEvent } = useMetaPixel();
  const { language, setLanguage } = useAuth();
  const couponCode = searchParams.get("coupon_code");

  useFirstPromoter(false);

  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const t = (key: string) => dictionary[key]?.[language as Language] ?? key;

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChangeLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem("user_language", lang);
  };

  const handleSendLeadToFP = () => {
    const normalizedEmail = email.trim();
    if (validateEmail(normalizedEmail)) {
      const fpr = (window as any).fpr;
      if (typeof fpr === "function") {
        fpr("referral", { email: normalizedEmail });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      setError(t("email-required"));
      return;
    }
    if (!validateEmail(normalizedEmail)) {
      setError(t("email-invalid"));
      return;
    }

    setError("");
    setIsLoading(true);

    const clientReferenceId = localStorage.getItem("client_reference_id");
    const payload = {
      email: normalizedEmail,
      ...(couponCode ? { coupon_code: couponCode } : {}),
      ...(clientReferenceId ? { client_reference_id: clientReferenceId } : {}),
    };

    postRegisterWithOffer(payload)
      .then((res) => {
        trackEvent("InitiateCheckout", {
          ...(couponCode ? { coupon_code: couponCode } : {}),
        });
        localStorage.setItem("access_token", res.access_token);
        localStorage.setItem("refresh_token", res.access_token);
        const decodedEmail = decodeTokenService.getEmailFromToken();
        if (decodedEmail) {
          FirstPromoter.referral(decodedEmail);
        }
        window.location.href = res.payment_url;
      })
      .catch((requestError: unknown) => {
        const trigger = (requestError as { response?: { data?: { trigger?: string } } })
          ?.response?.data?.trigger;
        setError(t(trigger && OFFER_UNAVAILABLE_TRIGGERS.has(trigger)
          ? "offer-error-unavailable"
          : "offer-error-generic"));
        setIsLoading(false);
      });
  };

  return (
    <div className="min-h-screen flex flex-col items-center px-4 py-8 bg-gradient-to-br from-[#0064E1] via-[#0073EE] to-fluxi relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#4834d4]/20 rounded-full blur-3xl" />
      </div>

      {/* Language switcher */}
      <div className="absolute top-6 left-6 z-50">
        <LanguageSwitcher
          currentLanguage={language as Language}
          languages={Object.values(Language)}
          onChangeLanguage={handleChangeLanguage}
        />
      </div>

      {/* Content wrapper */}
      <div className="w-full max-w-[460px] flex flex-col items-center my-auto relative z-10">
        {/* Logo */}
        <img
          src={FluxiLogo}
          alt="Fluxi"
          className="h-14 mb-10 drop-shadow-lg"
        />

        {/* Card */}
        <div className="w-full bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl px-8 pt-10 pb-8">
          {/* Title */}
          <h1 className="text-2xl md:text-[26px] font-bold text-[#1E1B4B] leading-tight text-center mb-8">
            {t("offer-enter-email-title")}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email field */}
            <div>
              <label className="block text-xs font-bold text-[#0073EE] tracking-wider mb-2">
                {t("offer-email-label")}
              </label>
              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#0073EE]/50">
                  <Mail className="w-5 h-5" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError("");
                  }}
                  className={`w-full pl-12 pr-16 py-3.5 text-sm border-2 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#0073EE]/20 focus:border-[#0073EE] ${
                    error
                      ? "border-red-300 bg-red-50"
                      : "border-gray-200 hover:border-[#0073EE]/30 bg-gray-50/80"
                  }`}
                  placeholder={t("offer-email-placeholder")}
                  autoFocus
                  disabled={isLoading}
                />
                <div className="absolute right-3.5 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  <img
                    src="/assets/icons/fluxiLogoMinified.svg"
                    alt=""
                    className="w-5 h-5"
                  />
                  <Lock className="w-4 h-4 text-[#0073EE]/60" />
                </div>
              </div>
              {error && (
                <p className="mt-2 whitespace-pre-line text-xs text-red-600 font-medium">{error}</p>
              )}
            </div>

            {/* Warning box */}
            <div className="bg-[#FEF9E7] border border-[#F5E6A3] rounded-xl p-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <AlertTriangle className="w-5 h-5 text-[#D4A017]" />
                </div>
                <div>
                  <p className="text-sm text-[#5D5346] leading-relaxed">
                    {t("offer-email-warning")}
                  </p>
                  <p className="text-xs font-bold text-[#8B7355] tracking-wide mt-3 uppercase leading-relaxed">
                    {t("offer-email-warning-bold")}
                  </p>
                </div>
              </div>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              onMouseDown={handleSendLeadToFP}
              onTouchStart={handleSendLeadToFP}
              className="w-full bg-[#0073EE] text-white py-4 px-6 rounded-xl text-base font-semibold hover:bg-[#5A4BD1] focus:outline-none focus:ring-2 focus:ring-[#0073EE] focus:ring-offset-2 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-[#0073EE]/25 hover:shadow-xl hover:shadow-[#0073EE]/30"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  {t("offer-processing")}
                </>
              ) : (
                <>
                  {t("offer-continue")}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Footer */}
      <p className="mt-auto pt-16 text-sm text-white/50">
        &copy; 2026 {t("offer-all-rights-reserved")}
      </p>

      {/* Mascot */}
      <img
        src="https://fluxi.co/wp-content/uploads/2025/07/Logo-fluxi-zorro-mascota.webp"
        alt="Fluxi Mascota"
        className="fixed bottom-[-10px] right-12 w-28 h-28 object-contain"
      />
    </div>
  );
};

export default LandingOfferPage;
